import { Skeleton } from '@/components/ui/skeleton';
import {
  getGetAnalyticsSummaryQueryKey,
  getGetRevenueStatsQueryKey,
  getGetTrafficByDayQueryKey,
  useGetAnalyticsSummary,
  useGetRevenueStats,
  useGetTrafficByDay,
} from '@/hooks/use-api';
import { useQuery } from '@tanstack/react-query';
import {
  HiOutlineArrowTrendingUp,
  HiOutlineGlobeAlt,
  HiOutlineMegaphone,
  HiOutlineUsers,
} from 'react-icons/hi2';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = [
  '#0ea5e9',
  '#0369a1',
  '#38bdf8',
  '#7dd3fc',
  '#bae6fd',
  '#0a2540',
  '#94a3b8',
  '#475569',
];

const getApiBase = () => {
  const base = import.meta.env.BASE_URL?.replace(/\/$/, '') || '';
  return `${base}/api`;
};

interface ReferralRow {
  source: string;
  medium: string;
  campaign: string;
  visits: number;
}
interface UtmRow {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  visits: number;
}

export default function AdminAnalytics() {
  const { data: summary } = useGetAnalyticsSummary({
    query: { queryKey: getGetAnalyticsSummaryQueryKey() },
  });
  const { data: traffic } = useGetTrafficByDay({
    query: { queryKey: getGetTrafficByDayQueryKey() },
  });
  const { data: revenue } = useGetRevenueStats({
    query: { queryKey: getGetRevenueStatsQueryKey() },
  });

  const { data: referrals, isLoading: refLoading } = useQuery<ReferralRow[]>({
    queryKey: ['referral-sources'],
    queryFn: async () => {
      const res = await fetch(`${getApiBase()}/analytics/referral-sources`);
      return res.json();
    },
  });

  const { data: utmCampaigns, isLoading: utmLoading } = useQuery<UtmRow[]>({
    queryKey: ['utm-campaigns'],
    queryFn: async () => {
      const res = await fetch(`${getApiBase()}/analytics/utm-campaigns`);
      return res.json();
    },
  });

  const pieData = Array.isArray(referrals)
    ? referrals.reduce((acc: { name: string; value: number }[], r) => {
        const existing = acc.find(x => x.name === r.source);
        if (existing) existing.value += r.visits;
        else acc.push({ name: r.source, value: r.visits });
        return acc;
      }, [])
    : [];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <span className="w-5 h-px bg-[#0ea5e9]" />
          <span className="font-rajdhani font-bold text-[0.64rem] tracking-[0.3em] uppercase text-[#0ea5e9]">
            Analytics
          </span>
        </div>
        <h1 className="font-bebas text-[2.6rem] leading-none tracking-[0.03em] text-[#0a2540]">
          Traffic & Conversion
        </h1>
        <p className="font-sans font-light text-sm text-[#94a3b8] mt-1">
          Track where your visitors come from and how they convert to students.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            icon: HiOutlineGlobeAlt,
            label: 'Page Views (30d)',
            value: summary?.totalPageViews ?? 0,
          },
          {
            icon: HiOutlineUsers,
            label: 'Total Students',
            value: summary?.totalUsers ?? 0,
          },
          {
            icon: HiOutlineArrowTrendingUp,
            label: 'Conversions',
            value: summary?.totalAppointments ?? 0,
          },
          {
            icon: HiOutlineMegaphone,
            label: 'New This Week',
            value: summary?.recentSignups ?? 0,
          },
        ].map((card, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl p-6"
            style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
          >
            <div className="w-10 h-10 rounded-xl bg-[#e0f4fd] flex items-center justify-center mb-3">
              <card.icon className="w-5 h-5 text-[#0369a1]" />
            </div>
            <div className="font-bebas text-[2.2rem] leading-none text-[#0a2540] mb-1">
              {card.value.toLocaleString()}
            </div>
            <div className="font-rajdhani font-bold text-[0.62rem] tracking-[0.18em] uppercase text-[#94a3b8]">
              {card.label}
            </div>
          </div>
        ))}
      </div>

      {/* Traffic by day */}
      <div
        className="bg-white rounded-2xl p-7"
        style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
      >
        <div className="font-rajdhani font-bold text-[0.7rem] tracking-[0.24em] uppercase text-[#0ea5e9] mb-1">
          Traffic Trend
        </div>
        <div className="font-bebas text-2xl text-[#0a2540] mb-5">
          Daily Page Views — Last 30 Days
        </div>
        {traffic && Array.isArray(traffic) ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={traffic}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: 'DM Sans',
                  fontSize: 12,
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(10,37,64,0.15)',
                }}
              />
              <Line
                type="monotone"
                dataKey="views"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Skeleton className="h-52 rounded-xl" />
        )}
      </div>

      {/* Referral sources grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie chart */}
        <div
          className="bg-white rounded-2xl p-7"
          style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
        >
          <div className="font-rajdhani font-bold text-[0.7rem] tracking-[0.24em] uppercase text-[#0ea5e9] mb-1">
            Sources
          </div>
          <div className="font-bebas text-2xl text-[#0a2540] mb-5">
            Traffic by Referral Source
          </div>
          {refLoading ? (
            <Skeleton className="h-48 rounded-xl" />
          ) : pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                  fontSize={10}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    fontFamily: 'DM Sans',
                    fontSize: 12,
                    borderRadius: 8,
                    border: 'none',
                    boxShadow: '0 8px 32px rgba(10,37,64,0.15)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <p className="font-sans text-sm text-[#94a3b8]">
                No referral data yet. Traffic will be tracked as visitors
                arrive.
              </p>
            </div>
          )}
        </div>

        {/* Source breakdown table */}
        <div
          className="bg-white rounded-2xl p-7"
          style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
        >
          <div className="font-rajdhani font-bold text-[0.7rem] tracking-[0.24em] uppercase text-[#0ea5e9] mb-1">
            Breakdown
          </div>
          <div className="font-bebas text-2xl text-[#0a2540] mb-5">
            Source · Medium · Visits
          </div>
          {refLoading ? (
            <Skeleton className="h-48 rounded-xl" />
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {Array.isArray(referrals) && referrals.length > 0 ? (
                referrals.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2 border-b border-[rgba(0,0,0,0.05)] last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: COLORS[i % COLORS.length] }}
                      />
                      <div>
                        <span className="font-rajdhani font-bold text-[0.78rem] text-[#0a2540] capitalize">
                          {r.source}
                        </span>
                        <span className="font-sans text-[0.72rem] text-[#94a3b8] ml-2">
                          / {r.medium}
                        </span>
                        {r.campaign && r.campaign !== '' && (
                          <span className="font-sans text-[0.66rem] text-[#94a3b8] ml-2">
                            ({r.campaign})
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-bebas text-lg text-[#0a2540]">
                      {r.visits}
                    </span>
                  </div>
                ))
              ) : (
                <p className="font-sans text-sm text-[#94a3b8] py-4">
                  No data yet — visit the site with ?utm_source=instagram to
                  test tracking.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* UTM Campaigns */}
      <div
        className="bg-white rounded-2xl p-7"
        style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
      >
        <div className="font-rajdhani font-bold text-[0.7rem] tracking-[0.24em] uppercase text-[#0ea5e9] mb-1">
          Campaigns
        </div>
        <div className="font-bebas text-2xl text-[#0a2540] mb-2">
          UTM Campaign Performance
        </div>
        <p className="font-sans text-[0.78rem] text-[#94a3b8] mb-5">
          Track links with{' '}
          <code className="bg-[#f7fafd] px-1.5 py-0.5 rounded text-[#0369a1]">
            ?utm_source=instagram&utm_medium=social&utm_campaign=spring2025
          </code>{' '}
          to see data here.
        </p>
        {utmLoading ? (
          <Skeleton className="h-44 rounded-xl" />
        ) : Array.isArray(utmCampaigns) &&
          utmCampaigns.filter(r => r.utmCampaign !== 'none').length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={utmCampaigns
                .filter(r => r.utmCampaign !== 'none')
                .slice(0, 10)}
            >
              <XAxis
                dataKey="utmCampaign"
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: 'DM Sans',
                  fontSize: 12,
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(10,37,64,0.15)',
                }}
              />
              <Bar dataKey="visits" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-44 flex flex-col items-center justify-center gap-2">
            <HiOutlineMegaphone className="w-8 h-8 text-[#0ea5e9]" />
            <p className="font-sans text-sm text-[#94a3b8]">
              No campaign data yet. Add UTM parameters to your social media
              links.
            </p>
          </div>
        )}
      </div>

      {/* Revenue */}
      <div
        className="bg-white rounded-2xl p-7"
        style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
      >
        <div className="font-rajdhani font-bold text-[0.7rem] tracking-[0.24em] uppercase text-[#0ea5e9] mb-1">
          Revenue
        </div>
        <div className="font-bebas text-2xl text-[#0a2540] mb-5">
          Monthly Revenue Trend
        </div>
        {revenue && Array.isArray(revenue) && revenue.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: 'Rajdhani', fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => `$${v}`}
              />
              <Tooltip
                contentStyle={{
                  fontFamily: 'DM Sans',
                  fontSize: 12,
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 8px 32px rgba(10,37,64,0.15)',
                }}
                formatter={v => [`$${v}`, 'Revenue']}
              />
              <Bar dataKey="revenue" fill="#0a2540" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Skeleton className="h-44 rounded-xl" />
        )}
      </div>
    </div>
  );
}
